import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as actions from '@actions';
import { useDispatch } from 'react-redux';
import { Modal, Tooltip, Form, Checkbox } from 'ALIASED-antd';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import useCase from '@hooks/useCase';
import { Alert, Input, Rule } from 'antd';
import { useNotification } from '../../../contexts/NotificationContext/NotificationContext';
import { InfoCircleOutlined } from '@ant-design/icons';
import useUser from '@vl-core/hooks/useUser';
import DocmanConsentStatusWithTooltip from '@components/Clinicians/Docman/DocmanConsentStatusWithTooltip';
import { NHSNumberFormItem } from '@components/Patients/Registration/items/NHSNumberFormItem';
import { validate as validateNHS } from 'nhs-number-validator';

const CaseDetailsBlock = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;

  label {
    font-weight: bold;
    display: inline-block;
    margin-bottom: 0.5rem;
  }

  input:disabled {
    color: inherit;
  }
`;

const GroupSection = styled.div`
  border-radius: 0.5rem;
  background-color: #f9fafb;
  padding: 1rem;
  margin-bottom: 2rem;

  h4 {
    margin-bottom: 0;
  }

  p:not(:last-child) {
    margin-bottom: 0.5rem;
  }

  > :last-child {
    margin-bottom: 0;
  }
`;

type ManagePatientDocmanConsentProps = {
  isOpen: boolean;
  onCancel: () => void;
  caseId: number;
  patientId: string;
};

type FormValues = {
  docmanConsent: boolean;
  nhs_number?: string;
};

const ManagePatientDocmanConsentModal: React.FC<ManagePatientDocmanConsentProps> = ({
  caseId,
  patientId,
  isOpen,
  onCancel
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const user = useUser();
  const { t } = useTranslation('common');
  const { successNotification, errorNotification } = useNotification();
  const [form] = Form.useForm<FormValues>();
  const docmanConsent = Form.useWatch('docmanConsent', form);
  const nhsNumberValue = Form.useWatch('nhs_number', form);
  const initialFormValues = {
    docmanConsent: false
  };

  const [caseDetails, invalidateCaseDetails] = useCase(caseId);

  const { data: patientGPDetails } = useQuery({
    queryKey: ['getPatientGPDetails', patientId],
    queryFn: () => dispatch(actions.getGpDetails(patientId)),
    enabled: !!patientId
  });

  const {
    addline1,
    addline2,
    addline3,
    addline4,
    region,
    postcode,
    practice_name,
    nhs_number: patientNHSNumber
  } = patientGPDetails || {};

  const practiceAddress = [addline1, addline2, addline3, addline4, region, postcode].filter(Boolean).join(', ');

  const { mutate: updateConsentMutation, isPending: isConsentUpdating } = useMutation({
    mutationFn: async () => {
      if (!caseDetails) throw new Error('Missing case details');

      const nhsNumber = form.getFieldValue('nhs_number');
      const result = await dispatch(
        actions.updatePatientDocmanConsent({
          caseId: caseDetails.case_id,
          clinicianId: user.user_guid,
          docmanConsent: 1,
          nhsNumber
        })
      );

      if (result?.error) {
        throw new Error(result.error.message || t('Error while changing patient consent'));
      }

      return result;
    },
    onSuccess: () => {
      successNotification({
        message: 'Success',
        description: t('Patient consent has been changed')
      });
      invalidateCaseDetails();
      queryClient.invalidateQueries({ queryKey: ['getPatientGPDetails', patientId] });
      onCancel();
    },
    onError: (error: any) => {
      errorNotification({
        message: 'Error',
        description: error?.message || t('Error while changing patient consent')
      });
      console.error('Update docman request failed', error);
    }
  });

  const onFormSubmit = async () => {
    try {
      await form.validateFields();
      updateConsentMutation();
    } catch (error) {
      console.log('Validation error: ', error);
    }
  };

  const validateDocmanConsent: Rule = {
    validator: async (_, value?: boolean) => {
      if (value === true) {
        return;
      }

      throw new Error(
        t('Please indicate whether patient consents to sharing all relevant documentation with their NHS GP')
      );
    }
  };

  const isSubmitDisabled = isConsentUpdating || !docmanConsent || !nhsNumberValue || !validateNHS(nhsNumberValue);

  return (
    <Modal
      open={isOpen}
      okText={t('Update Consent')}
      onOk={onFormSubmit}
      okButtonProps={{
        disabled: isSubmitDisabled
      }}
      closable
      centered
      onCancel={onCancel}
      width={950}
      title={<h2>{t('Consent to Share Clinical Records')}</h2>}
    >
      <CaseDetailsBlock>
        <div>
          <label htmlFor="consent-patient-name">{t('Patient Name')}:</label>
          <Input value={caseDetails?.patient} id="consent-patient-name" disabled />
        </div>

        <div>
          <label htmlFor="consent-case-speciality">{t('Speciality')}:</label>
          <Input value={caseDetails?.cat_desc} id="consent-case-speciality" disabled />
        </div>

        <div>
          <label htmlFor="consent-case-ref">{t('Case Number')}:</label>
          <Input value={caseDetails?.client_case_id} id="consent-case-ref" disabled />
        </div>
      </CaseDetailsBlock>

      <h3>
        {t("Patient's current GP details")}{' '}
        <span>
          <Tooltip
            title={t(
              "If the patient's current GP details are incorrect, please advise the patient to update their GP and consent status in their case documents."
            )}
            trigger={['hover', 'focus']}
          >
            <InfoCircleOutlined />
          </Tooltip>
        </span>
      </h3>

      <GroupSection>
        <h4>{t('Practice Name')}:</h4>
        <p>{practice_name}</p>

        <h4>{t('Practice Address')}:</h4>
        <p>{practiceAddress}</p>
      </GroupSection>

      <h3>{t('Update Consent')}</h3>
      <GroupSection>
        <p>
          {t(
            'Patients can consent to the automatic sharing of clinical letters and reports with their GP via Docman, a secure electronic document management system. All documents within this case will be shared after the consent is given.'
          )}
        </p>

        <h4>
          {t('Patient Consent Status')}:{' '}
          <DocmanConsentStatusWithTooltip
            docmanActive={caseDetails?.docman_active}
            consentGiven={caseDetails?.docman_consent}
            style={{ fontWeight: 'normal' }}
          />
        </h4>
      </GroupSection>

      <Form
        onFinish={onFormSubmit}
        form={form}
        layout="vertical"
        initialValues={initialFormValues}
        validateTrigger="submit"
      >
        <>
          <NHSNumberFormItem
            FormItemComponent={Form.Item}
            form={form}
            nhsNumberFieldName="nhs_number"
            patientNHSNumber={patientNHSNumber ?? undefined}
            showFindLink={false}
            hideValidationError
          />
          {!patientNHSNumber && (
            <Alert
              type="info"
              showIcon
              message=""
              description={t(
                "If the patient doesn't have their NHS number, they can go into their settings after the appointment, add it, and then manage their consent."
              )}
              style={{ marginBottom: '1em' }}
            />
          )}
        </>

        <Form.Item name="docmanConsent" valuePropName="checked" rules={[validateDocmanConsent]}>
          <Checkbox>
            {t(
              'The patient consents to automatically sharing all relevant documentation relating to their case with their NHS GP'
            )}
          </Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ManagePatientDocmanConsentModal;
